import { CanvasContext } from "client/contexts/ResizeEditor/Canvas";
import { calculateCanvasImageDimensions } from "client/utils/calculateCanvasImageDimensions";
import { downloadImageInFrontendTools } from "client/utils/downloadImageInFrontendTools";
import { loadImage } from "client/utils/imageEdit/loadImage";
import { loadSrcToImage } from "client/utils/loadSrcToImage";
import { translateFunc } from "client/utils/translateFunc";
import { DEFAULT_TOAST_SHOW_TIME } from "components/common/Toast/ToastRenderer";
import { useToast } from "components/common/Toast/useToast";
import { useContext, useEffect, useRef } from "react";
import { restoreState, setImage, setIsLoading } from "store/features/flipRotate/actions";
import { flipRotateSelector } from "store/features/flipRotate/selectors/flipRotateSelector";
import { FlipRotateState } from "store/features/flipRotate/types";
import { useAppDispatch, useAppSelector } from "store/store";

export const usePreview = () => {
    const { image, currentType, isLoading, upload } = useAppSelector(flipRotateSelector, equalityFn);
    const { parentImageRef, initialImageRef, flipRef, rotateRef, setImageLoaded } = useContext(CanvasContext);
    const previewContainerRef = useRef<HTMLDivElement | null>(null);
    const dispatch = useAppDispatch();
    const toast = useToast();
    const errorFromEditorText = translateFunc(`common:corrupt-image-toast-test`);

    const handleLoadImage = async () => {
        // imageLoaded check is to prevent unnecessary loading
        const parentImage = parentImageRef?.current;
        const previewContainer = previewContainerRef.current;
        const flipEl = flipRef?.current;
        const initialImage = initialImageRef?.current;
        if (!parentImage || !previewContainer || !flipEl || !initialImage) return;
        setImageLoaded(false);
        dispatch(setIsLoading(true));
        const { src } = await downloadImageInFrontendTools(upload, image.src, 0, () => {}, {}, image.ext);
        const { image: img, error: error } = await loadImage(src);

        if (error) {
            toast.toast({
                closeable: true,
                position: "top-right",
                status: "error",
                text: errorFromEditorText,
                time: DEFAULT_TOAST_SHOW_TIME,
            });
            setImageLoaded(true);
            dispatch(setIsLoading(false));
            dispatch(restoreState());
            return;
        }
        const { width, height } = setInitialImage(
            img,
            previewContainer.getBoundingClientRect().width,
            previewContainer.getBoundingClientRect().height,
            image.src,
        );
        parentImage.width = width;
        parentImage.height = height;
        await Promise.all([
            loadSrcToImage(src, parentImage),
            loadSrcToImage(src, flipEl),
            loadSrcToImage(src, initialImage),
        ]);
        flipEl.width = width;
        flipEl.height = height;
        initialImage.width = img.width;
        initialImage.height = img.height;

        setImageLoaded(true);
        dispatch(setIsLoading(false));
    };

    const setInitialImage = (
        img: HTMLImageElement,
        containerWidth: number,
        containerHeight: number,
        src: string,
    ): { width: number; height: number } => {
        const { width, height } = calculateCanvasImageDimensions(
            img.width,
            img.height,
            containerWidth,
            containerHeight,
            false,
        );

        dispatch(
            setImage({
                src,
                height: height,
                width: width,
                initialHeight: height,
                initialWidth: width,
                originalWidth: img.width,
                originalHeight: img.height,
                ratio: img.width / img.height,
                title: image.title,
                containerWidth,
                containerHeight,
                ext: image.ext,
            }),
        );

        return { width, height };
    };

    useEffect(() => {
        if (!parentImageRef?.current || !image.src) return;
        handleLoadImage();
    }, [parentImageRef, image.src]);

    return {
        image,
        flipRef,
        rotateRef,
        previewContainerRef,
        parentImageRef,
        initialImageRef,
        currentType,
        isLoading,
    };
};

const equalityFn = (left: FlipRotateState, right: FlipRotateState) => {
    if (
        JSON.stringify(left.image) !== JSON.stringify(right.image) ||
        left.currentType !== right.currentType ||
        left.isLoading !== right.isLoading
    )
        return false;

    return true;
};
